import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {environment} from '../../../../environments/environment';
import {IApiKeyOptions} from '../../../models/client-options.model';

/**
 * Ensures the URL is properly constructed by joining the base URL and path.
 * @param baseUrl The base URL.
 * @param path The path to append.
 * @returns The properly constructed URL.
 */
function joinUrl(baseUrl: string, path: string): string {
    // Ensure baseUrl ends with a slash and path doesn't start with a slash
    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
    return `${normalizedBaseUrl}${normalizedPath}`;
}

/**
 * Service for managing API keys through the Shine Auth service.
 */
@Injectable({
    providedIn: 'root'
})
export class ShineApiKeyService {
    private http = inject(HttpClient);

    private readonly baseUrl: string;

    /**
     * Initializes a new instance of the ShineApiKeyService class.
     * @param http The HTTP client.
     */
    constructor() {
        this.baseUrl = environment.authBaseServerUrl || 'https://auth.shine.intuvio.com';
        console.log(`ShineApiKeyService initialized with base URL: ${this.baseUrl}`);
    }

    /**
     * Gets all API keys for a client.
     * @param clientId The client ID.
     * @returns An observable that resolves to an array of API keys.
     */
    getApiKeys(clientId: string): Observable<IApiKeyOptions[]> {
        return this.http.get<any>(joinUrl(this.baseUrl, `api/api-keys?clientId=${clientId}`))
            .pipe(
                map((response: any) => {
                    // Extract the data array from the response
                    return response.data || [];
                })
            );
    }

    /**
     * Creates a new API key.
     * @param apiKey The API key to create.
     * @returns An observable that resolves to the created API key.
     */
    createApiKey(apiKey: {
        name: string;
        clientId: string;
        expiresAt?: string;
        permissions?: string[];
    }): Observable<{ id: string; key: string }> {
        return this.http.post<any>(joinUrl(this.baseUrl, 'api/api-keys'), apiKey)
            .pipe(
                map((response: any) => {
                    // Extract the data from the response
                    return response.data;
                })
            );
    }

    /**
     * Deletes an API key.
     * @param id The ID of the API key to delete.
     * @param clientId Optional client ID to ensure the correct auth object is deleted.
     * @returns An observable that resolves to a success indicator.
     */
    deleteApiKey(id: string, clientId?: string): Observable<boolean> {
        const query = clientId ? `?clientId=${encodeURIComponent(clientId)}` : '';
        return this.http.delete<any>(joinUrl(this.baseUrl, `api/api-keys/${id}${query}`))
            .pipe(
                map((response: any) => {
                    // Extract the success status from the response
                    return response.status === 'success';
                })
            );
    }

    /**
     * Checks the health of an API key.
     * @param id The ID of the API key to check.
     * @returns An observable that resolves to a health status.
     */
    checkApiKeyHealth(id: string): Observable<{ healthy: boolean; message?: string }> {
        return this.http.get<any>(joinUrl(this.baseUrl, `api/api-keys/${id}/health`))
            .pipe(
                map((response: any) => {
                    // Extract the data from the response
                    return response.data || {healthy: false, message: 'No health data available'};
                })
            );
    }

    /**
     * Checks if an API key with the same name already exists for a client.
     * @param clientId The client ID.
     * @param name The API key name.
     * @returns An observable that resolves to a boolean indicating if a duplicate exists.
     */
    checkDuplicate(clientId: string, name: string): Observable<boolean> {
        return this.http.get<any>(joinUrl(this.baseUrl, `api/api-keys/check-duplicate?clientId=${clientId}&name=${name}`))
            .pipe(
                map((response: any) => {
                    // Extract the data from the response
                    return response.data || false;
                })
            );
    }
}
